import React, { useEffect, useState } from 'react';
import { Building, ChevronDown, Filter, Search, X, Image } from 'lucide-react';
import AccordionPanel from '../AccordionPanel';
import { parseMultiSelect, serializeMultiSelect } from '../../utils/filtersApi';

function MultiSelectSection({
  sectionKey,
  title,
  icon: Icon,
  filterValue,
  setFilterValue,
  defaultValue,
  catalog,
  openFilter,
  setOpenFilter,
  filterSearch,
  setFilterSearch,
  setCurrentPage,
}) {
  const getSelected = (currentVal) => parseMultiSelect(currentVal, defaultValue, catalog);
  const countSelected = () => (filterValue === defaultValue ? 0 : getSelected(filterValue).length);
  const isOptionSelected = (option) =>
    filterValue !== defaultValue && getSelected(filterValue).includes(option);

  const handleRemoveFilter = (valueToRemove) => {
    const values = getSelected(filterValue).filter((v) => v !== valueToRemove);
    if (values.length === 0) setFilterValue(defaultValue);
    else setFilterValue(serializeMultiSelect(values));
    if (setCurrentPage) setCurrentPage(1);
  };

  const toggleMultiSelectFilter = (option) => {
    if (option === defaultValue) {
      setFilterValue(defaultValue);
    } else {
      let selected = filterValue === defaultValue ? [] : getSelected(filterValue);
      if (selected.includes(option)) {
        selected = selected.filter((x) => x !== option);
      } else {
        selected.push(option);
      }
      if (selected.length === 0) setFilterValue(defaultValue);
      else setFilterValue(serializeMultiSelect(selected));
    }
    if (setCurrentPage) setCurrentPage(1);
  };

  const handleSelectAllToggle = () => {
    const selected = filterValue === defaultValue ? [] : getSelected(filterValue);
    if (selected.length === catalog.length) setFilterValue(defaultValue);
    else setFilterValue(serializeMultiSelect(catalog));
    if (setCurrentPage) setCurrentPage(1);
  };

  const searchLower = filterSearch.toLowerCase();
  const filteredCatalog = catalog.filter((c) => c.toLowerCase().includes(searchLower));

  return (
    <div className="accordion-item">
      <div
        className="accordion-header"
        onClick={() => {
          setOpenFilter(openFilter === sectionKey ? null : sectionKey);
          setFilterSearch('');
        }}
      >
        <div className="accordion-header-left">
          <Icon size={18} className="accordion-header-icon" />
          <span className="accordion-header-title">{title}</span>
        </div>
        <div className="accordion-header-right">
          {filterValue !== defaultValue && (
            <span key={countSelected()} className="accordion-selected-count">
              {countSelected()}
            </span>
          )}
          <ChevronDown
            size={16}
            className={`accordion-chevron ${openFilter === sectionKey ? 'is-open' : ''}`}
          />
        </div>
      </div>

      <AccordionPanel isOpen={openFilter === sectionKey}>
        <div className="custom-dropdown-header accordion-search-container">
          <div className="custom-dropdown-search-wrap">
            <Search size={16} className="custom-dropdown-search-icon" />
            <input
              type="text"
              className="custom-dropdown-search-input accordion-search-input"
              placeholder={`Search ${title.toLowerCase()}`}
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="custom-dropdown-item select-all" onClick={handleSelectAllToggle}>
          <input
            type="checkbox"
            className="custom-dropdown-checkbox"
            checked={
              filterValue !== defaultValue && countSelected() === catalog.length
            }
            readOnly
          />
          <span>Select all</span>
        </div>
        <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
          {filteredCatalog.map((option) => (
            <div
              key={option}
              className="custom-dropdown-item"
              onClick={() => toggleMultiSelectFilter(option)}
            >
              <input
                type="checkbox"
                className="custom-dropdown-checkbox"
                checked={isOptionSelected(option)}
                readOnly
              />
              <span>{option}</span>
            </div>
          ))}
        </div>
        {filterValue !== defaultValue && (
          <div className="filters-badge-container">
            {getSelected(filterValue).map((item) => (
              <div key={item} className="filters-badge">
                <span>{item}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveFilter(item)}
                  className="filters-badge-cancel"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </AccordionPanel>
    </div>
  );
}

/**
 * Filters drawer for Nexus MI tables (Marketing, News/PR) — same UX as Filings Insights/Table.
 */
export default function MiTableFiltersDrawer({
  isOpen,
  onClose,
  filterCompany,
  setFilterCompany,
  companyCatalog = [],
  filterMediaType,
  setFilterMediaType,
  mediaTypeCatalog = [],
  showMediaType = false,
  setCurrentPage,
}) {
  const [draftCompany, setDraftCompany] = useState(filterCompany);
  const [draftMediaType, setDraftMediaType] = useState(filterMediaType);
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDraftCompany(filterCompany);
      setDraftMediaType(filterMediaType);
      setOpenFilter(null);
      setFilterSearch('');
    }
  }, [isOpen, filterCompany, filterMediaType]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.accordion-item') && !e.target.closest('.react-datepicker')) {
        setOpenFilter(null);
        setFilterSearch('');
      }
    };
    if (isOpen) document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isOpen]);

  const handleReset = () => {
    setDraftCompany('All companies');
    if (showMediaType) setDraftMediaType('All media types');
  };

  const handleApply = () => {
    setFilterCompany(draftCompany);
    if (showMediaType && setFilterMediaType) setFilterMediaType(draftMediaType);
    if (setCurrentPage) setCurrentPage(1);
    onClose();
  };

  return (
    <>
      <div className={`drawer-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <div className={`details-drawer filters-details-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h3 className="drawer-title filters-drawer-title-wrap">
            <Filter size={18} />
            <span>Filters</span>
          </h3>
          <button type="button" className="drawer-close filters-drawer-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="drawer-body filters-drawer-body">
          <MultiSelectSection
            sectionKey="company"
            title="Company"
            icon={Building}
            filterValue={draftCompany}
            setFilterValue={setDraftCompany}
            defaultValue="All companies"
            catalog={companyCatalog}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            filterSearch={filterSearch}
            setFilterSearch={setFilterSearch}
            setCurrentPage={null}
          />
          {showMediaType && setFilterMediaType ? (
            <MultiSelectSection
              sectionKey="mediaType"
              title="Media type"
              icon={Image}
              filterValue={draftMediaType}
              setFilterValue={setDraftMediaType}
              defaultValue="All media types"
              catalog={mediaTypeCatalog}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              filterSearch={filterSearch}
              setFilterSearch={setFilterSearch}
              setCurrentPage={null}
            />
          ) : null}
        </div>
        <div className="drawer-footer filters-drawer-footer">
          <button type="button" className="btn btn-sm filters-drawer-btn-reset" onClick={handleReset}>
            Reset All
          </button>
          <button type="button" className="btn btn-sm filters-drawer-btn-apply" onClick={handleApply}>
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
}
